import Layout from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock,
  Send,
  Facebook,
  Instagram,
  Twitter,
  Zap
} from "lucide-react";
import contactHeroBg from "@/assets/contact-hero-bg.jpg";
import { useSiteConfig } from "@/hooks/useSiteConfig";

const Contact = () => {
  const { getConfig } = useSiteConfig();
  
  const contactInfo = [
    { icon: Phone, title: "Phone Number", details: [getConfig('contact_phone', '+1 (809) 123-4567')], description: "Available 24/7 for bookings and emergencies" },
    { icon: Mail, title: "Email Address", details: [getConfig('contact_email', 'info@smartpuntacanatransfer.com')], description: "We respond within 2 hours during business hours" },
    { icon: MapPin, title: "Location", details: [getConfig('contact_location', 'Punta Cana')], description: "Serving all of Punta Cana and surrounding areas" },
    { icon: Clock, title: "Business Hours", details: ["24/7 Service Available"], description: "Emergency and airport transfers available anytime" }
  ];

  const serviceAreas = ["Punta Cana", "Bavaro", "Cap Cana", "Uvero Alto", "La Romana", "Santo Domingo", "Samaná", "Puerto Plata"];

  const socialLinks = [
    { icon: Facebook, name: "Facebook", url: getConfig('social_facebook')?.url || '#', enabled: getConfig('social_facebook')?.enabled || false },
    { icon: Instagram, name: "Instagram", url: getConfig('social_instagram')?.url || '#', enabled: getConfig('social_instagram')?.enabled || false },
    { icon: Twitter, name: "TikTok", url: getConfig('social_tiktok')?.url || '#', enabled: getConfig('social_tiktok')?.enabled || false }
  ].filter(social => social.enabled);

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${contactHeroBg})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-primary/85 via-primary/70 to-primary/90" />
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 right-20 w-72 h-72 bg-secondary/15 rounded-full blur-3xl" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white animate-fade-in">
            <Badge className="mb-6 bg-secondary/90 text-secondary-foreground border-0 text-sm px-6 py-2 rounded-full">Contact Us</Badge>
            <h1 className="text-5xl md:text-7xl font-black mb-6 leading-[0.9]">
              Let's <span className="text-secondary">Connect</span>
            </h1>
            <p className="text-xl text-white/80 font-light max-w-2xl mx-auto">
              Ready for your perfect transfer? Contact Smart Punta Cana Transfer for premium transportation.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-20 bg-background">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-secondary to-accent" />
        <div className="container mx-auto px-4">
          <div className="text-center mb-16 animate-fade-in">
            <Badge className="mb-4 bg-primary/10 text-primary border-primary/20 rounded-full px-6 py-2">Contact Information</Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">We're Here to <span className="text-secondary">Help</span></h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => (
              <Card key={index} className="text-center hover-lift border-0 shadow-md overflow-hidden">
                <div className="h-1 bg-gradient-to-r from-primary to-secondary" />
                <CardHeader>
                  <div className="mx-auto w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-2xl flex items-center justify-center mb-4 shadow-lg">
                    <info.icon className="h-7 w-7 text-white" />
                  </div>
                  <CardTitle className="text-lg">{info.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  {info.details.map((detail, detailIndex) => (
                    <div key={detailIndex} className="font-semibold text-primary mb-2">{detail}</div>
                  ))}
                  <CardDescription className="text-sm">{info.description}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <Badge className="mb-4 bg-secondary/10 text-secondary border-secondary/20 rounded-full px-6 py-2">Send Us a Message</Badge>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Quick <span className="text-primary">Contact Form</span>
              </h2>
              <p className="text-muted-foreground">
                Fill out the form and we'll get back to you within 2 hours during business hours
              </p>
            </div>

            <Card className="border-0 shadow-lg overflow-hidden">
              <div className="h-1 bg-gradient-to-r from-primary via-secondary to-accent" />
              <CardHeader>
                <CardTitle className="text-2xl text-center">Book or Inquire</CardTitle>
                <CardDescription className="text-center">
                  Whether you want to book a transfer or just have questions, we're here to help
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">First Name *</Label>
                      <Input id="firstName" placeholder="Your first name" required className="rounded-xl" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Last Name *</Label>
                      <Input id="lastName" placeholder="Your last name" required className="rounded-xl" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address *</Label>
                      <Input id="email" type="email" placeholder="your.email@example.com" required className="rounded-xl" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input id="phone" placeholder="+1 (xxx) xxx-xxxx" className="rounded-xl" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="service">Service Type *</Label>
                      <select 
                        id="service" 
                        className="w-full px-3 py-2 border border-input rounded-xl bg-background text-foreground"
                        required
                      >
                        <option value="">Select a service</option>
                        <option value="airport-transfer">Airport Transfer</option>
                        <option value="hotel-transfer">Hotel Transfer</option>
                        <option value="group-transport">Group Transportation</option>
                        <option value="general-inquiry">General Inquiry</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="date">Preferred Date</Label>
                      <Input id="date" type="date" className="rounded-xl" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="guests">Number of Guests</Label>
                    <select 
                      id="guests" 
                      className="w-full px-3 py-2 border border-input rounded-xl bg-background text-foreground"
                    >
                      <option value="">Select number of guests</option>
                      <option value="1">1 Guest</option>
                      <option value="2">2 Guests</option>
                      <option value="3-4">3-4 Guests</option>
                      <option value="5-8">5-8 Guests</option>
                      <option value="9-15">9-15 Guests</option>
                      <option value="16+">16+ Guests</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Message *</Label>
                    <Textarea 
                      id="message" 
                      placeholder="Please provide details about your request..."
                      rows={5}
                      required
                      className="rounded-xl"
                    />
                  </div>

                  <Button className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground rounded-full text-lg py-6 font-bold shadow-secondary group">
                    <Send className="mr-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    Send Message
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-primary/10 text-primary border-primary/20 rounded-full px-6 py-2">Service Areas</Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Where We <span className="text-secondary">Operate</span></h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
            {serviceAreas.map((area, index) => (
              <Card key={index} className="text-center p-4 hover-lift border-0 shadow-sm">
                <div className="flex items-center justify-center gap-2">
                  <MapPin className="h-3 w-3 text-secondary" />
                  <span className="text-primary font-medium text-sm">{area}</span>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Emergency Contact */}
      <section className="py-20 bg-gradient-to-br from-primary via-primary to-accent text-white relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 left-10 w-40 h-40 bg-secondary/20 rounded-full blur-3xl" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <Zap className="h-12 w-12 mx-auto mb-4 text-secondary animate-float" />
            <h2 className="text-3xl md:text-4xl font-black mb-4">24/7 Emergency Line</h2>
            <p className="text-white/80 mb-6 font-light">
              For urgent assistance, emergency transportation, or last-minute bookings
            </p>
            <div className="text-4xl font-black mb-6 text-secondary">{getConfig('contact_phone', '+1 (809) 123-4567')}</div>
            <a href={`tel:${getConfig('contact_phone', '+1 (809) 123-4567')}`}>
              <Button size="lg" className="bg-secondary hover:bg-secondary/90 text-secondary-foreground text-lg px-8 rounded-full font-bold shadow-secondary">
                <Phone className="mr-2 h-5 w-5" />
                Call Now
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Social Media */}
      {socialLinks.length > 0 && (
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold mb-2">Stay Connected</h3>
              <p className="text-muted-foreground text-sm">Follow us on social media</p>
            </div>

            <div className="flex justify-center space-x-4">
              {socialLinks.map((social, index) => (
                <a 
                  key={index}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-2xl flex items-center justify-center text-white hover:from-secondary hover:to-tropical transition-all duration-300 hover:scale-110 shadow-lg"
                >
                  <social.icon className="h-6 w-6" />
                </a>
              ))}
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
};

export default Contact;
